import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import {
  Button,
  Card,
  ErrorPage,
  MetricCard,
  MetricStrip,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
  Tabs,
} from "@h2j-data-team/ui";

import { BackLink } from "@/components/back-link";
import { supportCode } from "@/components/error-screen";
import { QvdDiffFilesTable } from "@/components/qvd-analysis";
import { connectionQueryOptions } from "@/features/qlik-connections/api";
import { qvdAnalysisRunQueryOptions, qvdRunCompareQueryOptions } from "@/features/qvd-analysis/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import {
  deltaToneClass,
  formatBytes,
  formatSignedBytes,
  formatSignedCount,
  type DeltaDirection,
} from "@/lib/health-format";

type Side = "added" | "removed";

export const Route = createFileRoute("/_authenticated/qvd-analyses/$runId_/compare/$baseId")({
  staticData: { pageTitle: "breadcrumbs.qvdAnalyses", breadcrumb: "breadcrumbs.qvdRunCompare" },
  validateSearch: (search: Record<string, unknown>): { tab: Side } => ({
    tab: search.tab === "removed" ? "removed" : "added",
  }),
  component: QvdRunComparePage,
});

// A delta carries its sign and, by the direction that counts as an improvement,
// its color.
function Delta({
  delta,
  direction,
  children,
}: {
  delta: number;
  direction: DeltaDirection;
  children: string;
}) {
  return <span className={deltaToneClass(delta, direction)}>{children}</span>;
}

function QvdRunComparePage() {
  const { t } = useTranslation("qvd-analysis");
  const { t: tc } = useTranslation("common");
  const { formatDateTime } = useFormatDate();
  const { language } = useLocale();
  const { runId, baseId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  const diffQuery = useQuery(qvdRunCompareQueryOptions(runId, baseId));
  const currentQuery = useQuery(qvdAnalysisRunQueryOptions(runId));
  const baseQuery = useQuery(qvdAnalysisRunQueryOptions(baseId));
  const { data: connection } = useQuery({
    ...connectionQueryOptions(currentQuery.data?.connectionId ?? ""),
    enabled: !!currentQuery.data?.connectionId,
  });

  const back = <BackLink to="/qvd-analyses/$runId" params={{ runId }} label={t("compare.back")} />;

  if (diffQuery.isLoading || currentQuery.isLoading || baseQuery.isLoading) {
    return <PageSkeleton variant="detail" label={tc("list.loading")} />;
  }

  const error = diffQuery.error ?? currentQuery.error ?? baseQuery.error;
  const diff = diffQuery.data;
  const current = currentQuery.data;
  const base = baseQuery.data;

  if (error || !diff || !current || !base) {
    return (
      <ErrorPage
        kind="load-failed"
        header={<PageHeader eyebrow={t("compare.eyebrow")} title={t("title")} back={back} />}
        title={t("compare.error")}
        supportCode={supportCode(error, tc("error.supportCode"))}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void diffQuery.refetch();
              void currentQuery.refetch();
              void baseQuery.refetch();
            }}
          >
            <RefreshCw />
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  // The service orders the pair by finish time; follow its verdict rather than
  // assuming the route's :runId is the newer one.
  const from = diff.fromRunId === current.id ? current : base;
  const to = diff.toRunId === current.id ? current : base;

  const countDelta = diff.toFileCount - diff.fromFileCount;
  const bytesDelta = diff.toTotalBytes - diff.fromTotalBytes;
  const count = (value: number) => value.toLocaleString(language);
  const range = (a: string, b: string) => t("compare.kpi.range", { from: a, to: b });

  // Both runs share a scope, but a space can appear in only one of them (a space
  // emptied or added between runs), so the filter offers the union.
  const spaceOptions = [
    ...new Set(
      [from, to].flatMap((r) => (r.coverage?.footprintBySpace ?? []).map((s) => s.spaceName)),
    ),
  ].sort();

  const setTab = (next: Side) => navigate({ search: (prev) => ({ ...prev, tab: next }) });

  const sides: { side: Side; total: number }[] = [
    { side: "added", total: diff.addedCount },
    { side: "removed", total: diff.removedCount },
  ];

  return (
    <PageShell
      header={
        <PageHeader
          eyebrow={t("compare.eyebrow")}
          title={connection?.name || t("title")}
          back={back}
          meta={
            <span className="truncate font-mono">
              {[
                t("compare.kpi.range", {
                  from: formatDateTime(from.finishedAt ?? from.createdAt),
                  to: formatDateTime(to.finishedAt ?? to.createdAt),
                }),
                to.scope.spaces.length > 0
                  ? t("compare.spaceCount", { count: to.scope.spaces.length })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          }
          actions={
            <>
              <Button variant="outline" size="sm" asChild>
                <Link
                  to="/qvd-analyses/$runId"
                  params={{ runId: from.id }}
                  search={{ tab: "orphans" }}
                >
                  {t("compare.openBase")}
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link
                  to="/qvd-analyses/$runId"
                  params={{ runId: to.id }}
                  search={{ tab: "orphans" }}
                >
                  {t("compare.openCurrent")}
                </Link>
              </Button>
            </>
          }
        />
      }
    >
      <PageContent>
        <div className="flex flex-col gap-5">
          <Card className="px-5 py-3">
            <MetricStrip>
              <MetricCard
                label={t("compare.kpi.files")}
                value={
                  <Delta delta={countDelta} direction="neutral">
                    {formatSignedCount(countDelta, language)}
                  </Delta>
                }
                description={range(count(diff.fromFileCount), count(diff.toFileCount))}
              />
              <MetricCard
                label={t("compare.kpi.space")}
                value={
                  <Delta delta={bytesDelta} direction="lower-is-better">
                    {formatSignedBytes(bytesDelta)}
                  </Delta>
                }
                description={range(
                  formatBytes(diff.fromTotalBytes),
                  formatBytes(diff.toTotalBytes),
                )}
              />
              <MetricCard
                label={t("compare.kpi.added")}
                value={
                  <span
                    className={diff.addedCount > 0 ? "text-status-success-foreground" : "text-dim"}
                  >
                    {count(diff.addedCount)}
                  </span>
                }
                description={t("compare.kpi.addedSub", { size: formatBytes(diff.addedBytes) })}
              />
              <MetricCard
                label={t("compare.kpi.removed")}
                value={
                  <span
                    className={diff.removedCount > 0 ? "text-status-danger-foreground" : "text-dim"}
                  >
                    {count(diff.removedCount)}
                  </span>
                }
                description={t("compare.kpi.removedSub", { size: formatBytes(diff.removedBytes) })}
              />
            </MetricStrip>
          </Card>

          <Tabs
            label={t("compare.tabs.label")}
            value={tab}
            onValueChange={(next) => setTab(next as Side)}
            items={sides.map(({ side, total }) => ({
              value: side,
              label: (
                <span className="inline-flex items-center gap-1.5">
                  {t(`compare.tabs.${side}`)}
                  <span className="font-mono text-xs text-faint">{total}</span>
                </span>
              ),
              content: (
                <QvdDiffFilesTable
                  runId={runId}
                  baseId={baseId}
                  side={side}
                  spaceOptions={spaceOptions}
                  totalCount={total}
                />
              ),
            }))}
          />
        </div>
      </PageContent>
    </PageShell>
  );
}
